import { Stack, StackItem, Text } from '@fluentui/react'
import React, { useContext } from 'react'

import { Conversation } from '../../api/models'
import { AppStateContext } from '../../state/AppProvider'

import { isEmpty, isNil } from 'lodash'
import { ChatHistoryListItemGroups } from './ChatHistoryListItem'

interface ChatHistoryListProps {}

export interface GroupedChatHistory {
  timeFrame: string
  entries: Conversation[]
}

const daysBetween = (first: Date, second: Date) => {
  var firstDate = new Date(first.getFullYear(), first.getMonth(), first.getDate())
  var secondDate = new Date(second.getFullYear(), second.getMonth(), second.getDate())

  // Do the math.
  const daysDifference = (firstDate.getTime() - secondDate.getTime()) / (1000 * 60 * 60 * 24)

  // Round.
  return Math.round(daysDifference)
}

const groupByTimeFrame = (entries: Conversation[]) => {
  const groups: GroupedChatHistory[] = [
    { timeFrame: 'Pinned Conversation', entries: [] },
    { timeFrame: 'Today', entries: [] },
    { timeFrame: 'Yesterday', entries: [] },
    { timeFrame: 'Last 7 days', entries: [] },
    { timeFrame: 'Last 30 days', entries: [] },
  ]
  const currentDate = new Date()

  entries.forEach(entry => {
    const date = new Date(entry.created_at)
    const daysDifference = daysBetween(currentDate, date)
    const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' })
    const existingGroup = groups.find(group => group.timeFrame === monthYear)
    const formattedDate = date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })

    entry.title = isNil(entry.title) || isEmpty(entry.title) ? formattedDate : entry.title
    if (entry.pinned) {
      groups[0].entries.push(entry)
    } else if (daysDifference < 1) {
      groups[1].entries.push(entry)
    } else if (daysDifference === 1) {
      groups[2].entries.push(entry)
    } else if (daysDifference <= 7) {
      groups[3].entries.push(entry)
    } else if (daysDifference <= 30) {
      groups[4].entries.push(entry)
    } else {
      if (existingGroup) {
        existingGroup.entries.push(entry)
      } else {
        groups.push({ timeFrame: monthYear, entries: [entry] })
      }
    }
  })

  groups.sort((a, b) => {
    if (a.timeFrame === 'Pinned Conversation') {
      return -1
    } else if (b.timeFrame === 'Pinned Conversation') {
      return 1
    }
    // Check if either group has no entries and handle it
    else if (a.entries.length === 0 && b.entries.length === 0) {
      return 0 // No change in order
    } else if (a.entries.length === 0) {
      return 1 // Move 'a' to a higher index (bottom)
    } else if (b.entries.length === 0) {
      return -1 // Move 'b' to a higher index (bottom)
    }
    const dateA = new Date(a.entries[0].created_at)
    const dateB = new Date(b.entries[0].created_at)
    return dateB.getTime() - dateA.getTime()
  })

  groups.forEach(group => {
    group.entries.sort((a, b) => {
      const dateA = new Date(a.created_at)
      const dateB = new Date(b.created_at)
      return dateB.getTime() - dateA.getTime()
    })
  })

  const filteredGroups = groups.filter(group => group.entries.length > 0)

  return filteredGroups
}

const ChatHistoryList: React.FC<ChatHistoryListProps> = () => {
  const appStateContext = useContext(AppStateContext)
  const chatHistory = appStateContext?.state.chatHistory

  React.useEffect(() => {}, [appStateContext?.state.chatHistory])

  let groupedChatHistory
  if (chatHistory && chatHistory.length > 0) {
    groupedChatHistory = groupByTimeFrame(chatHistory)
  } else {
    return (
      <Stack
        horizontal
        horizontalAlign="center"
        verticalAlign="center"
        style={{ width: '100%', marginTop: 10 }}>
        <StackItem>
          <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
            <span>No chat history.</span>
          </Text>
        </StackItem>
      </Stack>
    )
  }

  return <ChatHistoryListItemGroups groupedChatHistory={groupedChatHistory} />
}

export default ChatHistoryList
